import { useQueryClient } from "@tanstack/react-query";
import { Menu, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, Outlet, useNavigate, useSearchParams } from "react-router";

import { RequireActiveBusiness, useOrganization } from "@/auth/organization";
import { useUser } from "@/auth/session";
import { NewBusinessModal } from "@/components/business/NewBusinessModal";
import { HelpProvider } from "@/components/help";
import { HelpMenu, OnboardingProvider, Tour } from "@/components/onboarding";
import { WhatsNewMenu } from "@/components/whats-new/WhatsNewMenu";
import { initials } from "@/lib/format";
import { useApplyDisplayTimeZone } from "@/lib/use-time-zone";

import { OrganizationSwitcher } from "./OrganizationSwitcher";
import { Avatar, Sidebar } from "./Sidebar";
import { MAIN_CONTENT_ID, SkipLink } from "./SkipLink";
import { VerifyEmailBanner } from "./VerifyEmailBanner";
import { Button } from "@/components/ui";

const COLLAPSED_KEY = "cortexgrip.portal.sidebar-collapsed";

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * Figma "Home" frame: dark navigation chrome, and the page in a rounded cream panel inset by 12px
 * — a header (business switcher, account) over a white, independently scrolling content area.
 */
export function AppShell() {
  return (
    <OnboardingProvider>
      <HelpProvider>
        <Shell />
      </HelpProvider>
    </OnboardingProvider>
  );
}

function Shell() {
  const user = useUser();
  const [mobileOpen, setMobileOpen] = useState(false);
  const mobileDialogRef = useRef<HTMLDivElement>(null);
  const mobileCloseRef = useRef<HTMLButtonElement>(null);
  const mobileTriggerRef = useRef<HTMLButtonElement>(null);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const { orgId, switchTo } = useOrganization();
  const timeZone = useApplyDisplayTimeZone();
  const navigate = useNavigate();
  const qc = useQueryClient();
  // `?new-business` opens "Add a new business" over the current page (old /onboarding?new=1 links land here).
  const [params, setParams] = useSearchParams();
  const newBusinessOpen = params.has("new-business");
  const setNewBusinessOpen = (open: boolean) =>
    setParams(
      (p) => {
        if (open) p.set("new-business", "");
        else p.delete("new-business");
        return p;
      },
      { replace: true },
    );
  const toggleCollapsed = () =>
    setCollapsed((v) => {
      try {
        localStorage.setItem(COLLAPSED_KEY, v ? "0" : "1");
      } catch {
        /* private mode: not remembered */
      }
      return !v;
    });

  useEffect(() => {
    if (!mobileOpen) return;
    const opener = mobileTriggerRef.current;
    mobileCloseRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMobileOpen(false);
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = mobileDialogRef.current?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      if (opener && document.contains(opener)) opener.focus();
    };
  }, [mobileOpen]);

  return (
    <div className="flex h-full bg-chrome print:block print:h-auto print:bg-surface">
      <SkipLink />
      <aside className="hidden shrink-0 lg:block print:hidden">
        <Sidebar collapsed={collapsed} onToggleCollapsed={toggleCollapsed} />
      </aside>

      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 lg:hidden"
          ref={mobileDialogRef}
          role="dialog"
          aria-modal="true"
          aria-label="Navigation"
          tabIndex={-1}
        >
          {/* A mouse shortcut: keyboard users close with Escape or "Close navigation". */}
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 left-0 max-w-[85%] bg-chrome shadow-xl">
            <Button
              unstyled
              ref={mobileCloseRef}
              type="button"
              className="absolute top-6 right-3 z-10 rounded p-1 text-chrome-fg-subtle hover:text-chrome-fg"
              onClick={() => setMobileOpen(false)}
              aria-label="Close navigation"
            >
              <X className="size-5" />
            </Button>
            <Sidebar onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 p-2 sm:p-3">
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-[20px] bg-canvas">
          <header className="relative flex items-center gap-3 p-3 sm:p-4 print:hidden">
            <Button
              unstyled
              ref={mobileTriggerRef}
              type="button"
              className="flex size-[38px] shrink-0 items-center justify-center rounded-[10px] border border-border bg-surface text-fg-muted lg:hidden"
              onClick={() => setMobileOpen(true)}
              aria-label="Open navigation"
            >
              <Menu className="size-5" />
            </Button>
            <OrganizationSwitcher onNewBusiness={() => setNewBusinessOpen(true)} />
            <span className="flex-1" />
            <WhatsNewMenu />
            <HelpMenu />
            <Link
              to="/settings"
              aria-label={`${initials(user.display_name || user.email)} account settings`}
              className="shrink-0"
            >
              <Avatar user={user} className="size-[38px] rounded-[10px]" />
            </Link>
          </header>
          <main
            // `relative` makes <main> the containing block for absolutely placed descendants (the hidden `sr-only`
            // reasons inside tooltip wrappers); without it they escape the scroll area and add a second, page scrollbar.
            id={MAIN_CONTENT_ID}
            tabIndex={-1}
            className="relative min-h-0 flex-1 overflow-y-auto bg-surface px-4 pt-5 pb-10 sm:px-6 print:overflow-visible"
          >
            <VerifyEmailBanner />
            {/* Remounted per business, so forms and drafts start over from the new business's data, and when the
                time zone times are written in changes, so every time on the page follows it. */}
            <RequireActiveBusiness>
              <Outlet key={`${orgId} ${timeZone}`} />
            </RequireActiveBusiness>
          </main>
        </div>
      </div>

      {newBusinessOpen && (
        <NewBusinessModal
          onClose={() => setNewBusinessOpen(false)}
          onCreated={(org) => {
            // As in the business switcher: nothing cached for the previous business may render.
            switchTo(org.id);
            void qc.invalidateQueries();
            navigate("/", { replace: true });
          }}
        />
      )}
      <Tour />
    </div>
  );
}
